import { DistanceUnit, getOptionTranslation } from "@keita/lanepatrol-sdk";
import { t } from "i18next";
import { ATTRIBUTE_LABELS } from "../../hla/models/ComparisonFields";
import { hlaAttributeOptions } from "../../map/models/HlaAttribute";

export const isEqual = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

export const humanizeKey = (key: string): string => {
  const withSpaces = key.replace(/([A-Z])/g, " $1").toLowerCase();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

export const attributeLabel = (key: string): string =>
  ATTRIBUTE_LABELS[key] ??
  t(`shared:hlaAttribute.${key}`, {
    defaultValue: humanizeKey(key),
  });

export const getSpeedUnit = (distanceUnit: DistanceUnit): string =>
  distanceUnit === DistanceUnit.Imperial ? "mph" : "km/h";

export const getOptionLabel = (value: unknown): string | null => {
  const optionGroups = Object.values(hlaAttributeOptions).filter(Boolean);

  for (const options of optionGroups) {
    const option = options.find((item) => item.value === value);
    if (option) return t(option.translation);
  }

  if (typeof value === "string") {
    const translation = getOptionTranslation(value as any);
    if (translation) return t(translation);
  }

  return null;
};

export const formatScalar = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return getOptionLabel(value) ?? String(value);
};

export const formatNumber = (value: number): string => value.toLocaleString();

export const getChangedObjectKeys = (
  value: unknown,
  other: unknown,
): string[] => {
  const valueObj = (value ?? {}) as Record<string, unknown>;
  const otherObj = (other ?? {}) as Record<string, unknown>;

  return Array.from(
    new Set([...Object.keys(valueObj), ...Object.keys(otherObj)]),
  ).filter((key) => !isEqual(valueObj[key], otherObj[key]));
};

export const formatValue = (value: unknown, other: unknown): string => {
  if (value === null || value === undefined) return "No attribute selected";

  if (typeof value === "object" && !Array.isArray(value)) {
    const valueObj = value as Record<string, unknown>;
    const changedKeys = getChangedObjectKeys(value, other);

    if (changedKeys.length === 0) return "No change";

    return changedKeys
      .map((key) => `${attributeLabel(key)}: ${formatScalar(valueObj[key])}`)
      .join("\n");
  }

  return formatScalar(value);
};

export const formatChangeValue = (
  changeKey: string,
  value: unknown,
  other: unknown,
  speedUnit: string,
): string => {
  const formattedValue = formatValue(value, other);

  if (
    changeKey === "averageRoadSpeed" &&
    value !== null &&
    value !== undefined
  ) {
    return `${formattedValue} ${speedUnit}`;
  }

  return formattedValue;
};
